import { Form, Row, Col, Select, FormInstance } from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { editExport, showExport } from '@app/api/export.api';
import { DropdownOption } from '@app/models/DropdownOption';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';

type Field = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'dropdown' | 'richtext' | 'boolean';
  options?: DropdownOption[];
  rules?: Record<string, any>;
};

type ExportAdditionalInfoProps = {
  exportId: number | null;
  editable: boolean;
  form: FormInstance;
};

const ExportAdditionalInfo: React.FC<ExportAdditionalInfoProps> = ({ exportId, editable, form }) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [exportInfoValues, setExportInfoValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);

  const fetchFields = useCallback(
    async (exportId: number) => {
      if (!exportId) return;

      try {
        setLoading(true);
        const response = await editExport(exportId);

        const exportFieldDefs: any[] = response?.export?.product_category?.export_fields?.EXPORT || [];
        const infoValues: Record<string, any> = response?.export?.export_info?.informations || {};

        if (editable) {
          const formattedFields: Field[] = exportFieldDefs.map((field: any) => ({
            key: field.field_id,
            label: field.name || 'Unnamed Field',
            type: field.input_type?.toLowerCase() || 'text',
            options: field.options
              ? JSON.parse(field.options).map((opt: string) => ({ value: opt, label: opt }))
              : undefined,
            rules: field.rules ? JSON.parse(field.rules) : {},
          }));

          setFields(formattedFields);

          form.setFieldsValue({
            export_info: formattedFields.reduce((acc, f) => {
              acc[f.key] = infoValues[f.key] ?? '';
              return acc;
            }, {} as Record<string, any>),
          });
        } else {
          const hasAnyValues = Object.keys(infoValues).length > 0;

          const formattedFields: Field[] = exportFieldDefs
            .filter((field: any) =>
              hasAnyValues
                ? infoValues[field.field_id] !== undefined &&
                  infoValues[field.field_id] !== null &&
                  infoValues[field.field_id] !== ''
                : true,
            )
            .map((field: any) => ({
              key: field.field_id,
              label: field.name || 'Unnamed Field',
              type: field.input_type?.toLowerCase() || 'text',
              options: field.options
                ? JSON.parse(field.options).map((opt: string) => ({ value: opt, label: opt }))
                : undefined,
              rules: {},
            }));

          setFields(formattedFields);
        }

        setExportInfoValues(infoValues);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching export fields:', error);
        setFields([]);
        setLoading(false);
      }
    },
    [form, editable],
  );

  useEffect(() => {
    if (exportId) {
      fetchFields(exportId);
    }
  }, [exportId, fetchFields]);

  if (loading) {
    return <p>Loading...</p>;
  }

  if (fields.length === 0) {
    return <p>No additional fields available.</p>;
  }

  return (
    <Row gutter={[16, 16]}>
      {fields.map((field) => {
        const displayValue = exportInfoValues[field.key];

        return (
          <Col key={field.key} span={12}>
            <Form.Item
              name={['export_info', field.key]}
              label={field.label}
              rules={field.rules?.required ? [{ required: true, message: `${field.label} is required` }] : []}
            >
              {editable ? (
                field.type === 'dropdown' ? (
                  <Select options={field.options} allowClear />
                ) : field.type === 'boolean' ? (
                  <Select
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                    allowClear
                  />
                ) : (
                  <BaseInput type={field.type} />
                )
              ) : (
                <span>
                  {field.type === 'dropdown' || field.type === 'boolean'
                    ? field.options?.find((opt) => opt.value === displayValue)?.label ||
                      (typeof displayValue === 'boolean' ? (displayValue ? 'Yes' : 'No') : '')
                    : displayValue?.toString() || ''}
                </span>
              )}
            </Form.Item>
          </Col>
        );
      })}
    </Row>
  );
};

export default ExportAdditionalInfo;
